// Busca global ⌘K (mockup Aeros): campo no header + paleta com Telas,
// Contatos (GET /contacts?searchParam) e Atendimentos (GET /tickets?searchParam).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import SearchIcon from "@material-ui/icons/Search";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import api from "../../services/api";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { initialsOf, avatarColorFor } from "../../pages/Contacts/contactRules";
import { matchScreens } from "./headerRules";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.08)";
  return {
    field: { display: "flex", alignItems: "center", gap: 8, backgroundColor: theme.palette.aeros.surface2 || theme.palette.background.paper, border: `1px solid ${line}`, borderRadius: 10, padding: "7px 12px", fontSize: 12.5, color: theme.palette.text.secondary, width: 240, cursor: "text", font: "inherit", textAlign: "left", "& .MuiSvgIcon-root": { fontSize: 17 }, [theme.breakpoints.down("sm")]: { display: "none" } },
    kbd: { fontSize: 10, fontWeight: 700, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`, borderRadius: 5, padding: "1px 5px", color: theme.palette.aeros.textTertiary },
    paper: { width: 560, maxWidth: "calc(100vw - 32px)", borderRadius: 14, backgroundColor: theme.palette.aeros.surface2 || theme.palette.background.paper, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`, boxShadow: "0 24px 60px rgba(0,0,0,0.6)", position: "absolute", top: 80, margin: 0 },
    input: { display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: `1px solid ${line}`, "& input": { flex: 1, background: "none", border: "none", outline: "none", font: "inherit", fontSize: 14, color: theme.palette.text.primary, "&::placeholder": { color: theme.palette.aeros.textTertiary } }, "& .MuiSvgIcon-root": { fontSize: 20, color: theme.palette.text.secondary } },
    body: { maxHeight: 420, overflowY: "auto", padding: 8, ...theme.scrollbarStyles },
    group: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, padding: "10px 10px 4px" },
    item: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 9, fontSize: 13, color: theme.palette.text.primary, background: "none", border: "none", width: "100%", cursor: "pointer", font: "inherit", textAlign: "left", "& .MuiSvgIcon-root": { fontSize: 16, color: theme.palette.aeros.textTertiary, marginLeft: "auto" } },
    itemOn: { backgroundColor: alpha("#A044FF", 0.12) },
    av: { width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: "#07080D", flex: "none" },
    sub: { fontSize: 11, color: theme.palette.text.secondary },
    path: { fontSize: 11, color: stateText(theme, AEROS.cyan, "info") },
    empty: { padding: 20, textAlign: "center", fontSize: 12.5, color: theme.palette.text.secondary },
  };
});

export default function HeaderSearch() {
  const classes = useStyles();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [cursor, setCursor] = useState(0);
  const reqRef = useRef(0);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(""); setContacts([]); setTickets([]); setCursor(0); return; }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setContacts([]); setTickets([]); return undefined; }
    const req = ++reqRef.current;
    const t = setTimeout(async () => {
      const [c, tk] = await Promise.all([
        api.get("/contacts", { params: { searchParam: q, pageNumber: 1 } }).then((r) => r.data.contacts || []).catch(() => []),
        api.get("/tickets", { params: { searchParam: q, pageNumber: 1, showAll: "true" } }).then((r) => r.data.tickets || []).catch(() => []),
      ]);
      if (req !== reqRef.current) return;
      setContacts(c.slice(0, 5)); setTickets(tk.slice(0, 5)); setCursor(0);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const items = useMemo(() => [
    ...matchScreens(q).map((s) => ({ kind: i18n.t("aeros.header.groups.screens"), screen: true, key: `s${s.path}`, label: s.label, sub: s.path, go: () => history.push(s.path) })),
    ...contacts.map((c) => ({ kind: i18n.t("aeros.header.groups.contacts"), key: `c${c.id}`, label: c.name, sub: c.number, av: true, go: () => history.push(`/contacts?search=${encodeURIComponent(c.name || "")}`) })),
    ...tickets.map((t) => ({ kind: i18n.t("aeros.header.groups.tickets"), key: `t${t.id}`, label: `#${t.id} · ${t.contact?.name || i18n.t("aeros.header.noContact")}`, sub: t.lastMessage || t.status, go: () => history.push(`/tickets/${t.uuid}`) })),
  ], [q, contacts, tickets, history]);

  const go = (it) => { setOpen(false); it.go(); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    if (e.key === "Enter" && items[cursor]) go(items[cursor]);
  };
  const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

  let lastKind = null;
  return (
    <>
      <button type="button" className={classes.field} onClick={() => setOpen(true)} aria-label={i18n.t("aeros.header.search")}>
        <SearchIcon /><span style={{ flex: 1 }}>{i18n.t("aeros.header.search")}</span><span className={classes.kbd}>{isMac ? "⌘K" : "Ctrl K"}</span>
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: classes.paper }} maxWidth={false}>
        <div className={classes.input}>
          <SearchIcon />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder={i18n.t("aeros.header.searchPlaceholder")} aria-label={i18n.t("aeros.header.searchAria")} />
          <span className={classes.kbd}>Esc</span>
        </div>
        <div className={classes.body}>
          {!items.length && <div className={classes.empty}>{i18n.t("aeros.header.nothingFound", { q })}</div>}
          {items.map((it, i) => {
            const head = it.kind !== lastKind; lastKind = it.kind;
            return (
              <React.Fragment key={it.key}>
                {head && <div className={classes.group}>{it.kind}</div>}
                <button type="button" className={`${classes.item} ${i === cursor ? classes.itemOn : ""}`} onMouseEnter={() => setCursor(i)} onClick={() => go(it)}>
                  {it.av && <div className={classes.av} style={{ backgroundColor: avatarColorFor(it.label || "") }}>{initialsOf(it.label || "?")}</div>}
                  <span>{it.label}</span>
                  <span className={it.screen ? classes.path : classes.sub}>{it.sub}</span>
                  <ArrowForwardIcon />
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </Dialog>
    </>
  );
}
